import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { TreeStructure, Warning, Star } from '@phosphor-icons/react';
import { API } from '@/App';

var HEALTH_COLORS = { healthy: '#1D9E75', degraded: '#C8A030', error: '#E24B4A', unknown: '#3D5A70' };
var EDGE_COLORS = { job: '#0ABFA3', webhook: '#4285F4', query: '#D4A574', transform: '#7F77DD', manual: '#3D5A70' };
var CATEGORY_ICONS = { payment: '$', crm: 'C', devops: 'D', communication: 'M', storage: 'S', analytics: 'A', custom: 'K' };

function circularLayout(nodes, width, height) {
  var cx = width / 2, cy = height / 2;
  var r = Math.min(width, height) * 0.35;
  return nodes.map(function (n, i) {
    var a = (2 * Math.PI * i) / nodes.length - Math.PI / 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  });
}

export default function SharedMapView() {
  var { token } = useParams();
  var [data, setData] = useState(null);
  var [error, setError] = useState(null);
  var [loading, setLoading] = useState(true);
  var CANVAS_W = 1200, CANVAS_H = 600;

  useEffect(function () {
    fetch(API + '/map/shared/' + token)
      .then(function (r) {
        if (!r.ok) throw new Error(r.status === 410 ? 'This shared link has expired' : 'Link not found');
        return r.json();
      })
      .then(function (d) { setData(d.map); })
      .catch(function (e) { setError(e.message); })
      .finally(function () { setLoading(false); });
  }, [token]);

  if (loading) return <div className="min-h-screen bg-kova-bg flex items-center justify-center"><span className="font-mono text-kova-cyan">Loading shared map...</span></div>;
  if (error) return <div className="min-h-screen bg-kova-bg flex items-center justify-center flex-col gap-3"><Warning size={32} className="text-kova-red" /><p className="font-mono text-kova-silv text-sm">{error}</p></div>;
  if (!data || !data.nodes || data.nodes.length === 0) return <div className="min-h-screen bg-kova-bg flex items-center justify-center"><TreeStructure size={40} className="text-kova-cyan/12" /></div>;

  var nodes = data.nodes;
  var edges = data.edges;
  var positions = circularLayout(nodes, CANVAS_W, CANVAS_H);
  var nodeIndex = {};
  nodes.forEach(function (n, i) { nodeIndex[n.node_id] = i; });

  return (
    <div className="min-h-screen bg-kova-bg p-6" data-testid="shared-map-view">
      <div className="max-w-[1280px] mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-lg text-white">KOVA — Shared Integration Map</h1>
            <p className="text-[10px] font-mono text-kova-slate mt-0.5">Read-only view · {nodes.length} nodes · {edges.length} edges</p>
          </div>
          <span className="text-[9px] font-mono px-2 py-1 bg-kova-cyan/10 text-kova-cyan border border-kova-cyan/20 uppercase">shared</span>
        </div>
        <div className="border border-kova-cyan/12 overflow-hidden" style={{ height: CANVAS_H + 40 }}>
          <svg width="100%" height={CANVAS_H + 40} viewBox={'0 0 ' + CANVAS_W + ' ' + (CANVAS_H + 40)} className="bg-kova-bg">
            <defs>
              <pattern id="grid-shared" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(10,191,163,0.12)" strokeWidth="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-shared)" />
            {edges.map(function (e) {
              var si = nodeIndex[e.source_node_id], ti = nodeIndex[e.target_node_id];
              if (si == null || ti == null) return null;
              var color = EDGE_COLORS[e.edge_type] || 'rgba(10,191,163,0.12)';
              var dx = positions[ti].x - positions[si].x, dy = positions[ti].y - positions[si].y;
              var len = Math.sqrt(dx * dx + dy * dy) || 1;
              var ux = dx / len, uy = dy / len;
              return <line key={e.edge_id} x1={positions[si].x + ux * 64} y1={positions[si].y + uy * 28} x2={positions[ti].x - ux * 64} y2={positions[ti].y - uy * 28} stroke={color} strokeWidth={1} opacity={0.6} />;
            })}
            {nodes.map(function (n, i) {
              var healthColor = HEALTH_COLORS[n.health_status] || HEALTH_COLORS.unknown;
              var catIcon = CATEGORY_ICONS[n.category] || 'K';
              return (
                <g key={n.node_id} transform={'translate(' + positions[i].x + ',' + positions[i].y + ')'}>
                  <rect x={-60} y={-24} width={120} height={48} rx={4} fill="#0C1220" stroke={n.is_critical_path ? '#FFD700' : 'rgba(10,191,163,0.12)'} strokeWidth={1} />
                  <circle cx={-46} cy={-10} r={3} fill={healthColor} />
                  <text x={-38} y={-6} fill="#7A9AB0" fontSize="10" fontFamily="monospace" textAnchor="start">
                    {n.label.length > 14 ? n.label.slice(0, 13) + '\u2026' : n.label}
                  </text>
                  <text x={-46} y={10} fill="#4A6077" fontSize="8" fontFamily="monospace">{n.provider || n.node_type}</text>
                  <text x={42} y={-6} fill="rgba(10,191,163,0.12)" fontSize="14" fontFamily="monospace" fontWeight="bold" textAnchor="end">{catIcon}</text>
                </g>
              );
            })}
          </svg>
        </div>
        <div className="flex items-center gap-4 text-[8px] font-mono text-kova-slate">
          {Object.entries(EDGE_COLORS).map(function (e) { return <span key={e[0]} className="flex items-center gap-1"><span className="w-3 h-0.5" style={{ background: e[1] }} />{e[0]}</span>; })}
        </div>
      </div>
    </div>
  );
}
